import { type ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, UserCog, X } from 'lucide-react';
import { Button } from '@shared/ui/button';
import { cn } from '@shared/lib/cn';
import { useCoachContext } from '@shared/lib/coach-context-store';

type BackOption = boolean | { readonly to: string };

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  /**
   * Render a leading back button. `true` uses router history (browser-style
   * back), `{ to }` navigates to an explicit path. Omit on root routes.
   */
  back?: BackOption;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  actions,
  back,
  className,
}: PageHeaderProps): React.JSX.Element {
  return (
    <header
      className={cn(
        // Stronger blur (`xl`) + lower opacity bg so the page content visibly
        // softens behind the header on scroll. Hairline bottom border keeps
        // the header anchored even before the content scrolls under it.
        'border-border bg-background/80 sticky top-0 z-10 border-b backdrop-blur-xl',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3 px-4 py-4">
        {back ? <BackButton back={back} /> : null}
        <div className="min-w-0 flex-1 space-y-0.5">
          <h1 className="text-h1 truncate font-semibold tracking-tight" title={title}>
            {title}
          </h1>
          {subtitle ? <p className="text-muted-foreground text-caption">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      <CoachContextStrip />
    </header>
  );
}

/**
 * Compact strip under the title showing the active coach context. Renders
 * nothing when not in coach mode. Lives inside PageHeader so it sticks to the
 * top of the viewport alongside the title.
 */
function CoachContextStrip(): React.JSX.Element | null {
  const { t } = useTranslation();
  const active = useCoachContext((s) => s.active);
  const exit = useCoachContext((s) => s.exit);
  if (!active) return null;
  return (
    <div className="flex items-center justify-between gap-2 border-t border-amber-500/40 bg-amber-500/15 px-4 py-1.5 text-xs">
      <span
        className="flex min-w-0 items-center gap-1.5 truncate text-amber-200 dark:text-amber-200"
        title={t('coachContext.banner.coachingAs', { name: active.athleteName })}
      >
        <UserCog className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">
          {t('coachContext.banner.coachingAs', { name: active.athleteName })}
        </span>
      </span>
      <button
        type="button"
        onClick={exit}
        aria-label={t('coachContext.banner.exit')}
        className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-0.5 font-medium text-amber-100 transition-colors hover:bg-amber-500/25 focus-visible:bg-amber-500/25 focus-visible:outline-none"
      >
        <X className="size-3" aria-hidden="true" />
        {t('coachContext.banner.exit')}
      </button>
    </div>
  );
}

function BackButton({ back }: { back: BackOption }): React.JSX.Element {
  const router = useRouter();
  const { t } = useTranslation();
  const explicit = typeof back === 'object' ? back.to : null;
  return (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      className="-ml-2 shrink-0"
      aria-label={t('common.back')}
      onClick={() => {
        if (explicit) {
          void router.navigate({ to: explicit });
        } else {
          router.history.back();
        }
      }}
    >
      <ChevronLeft className="size-5" />
    </Button>
  );
}
